/**
 * InfoTooltip — Icono "ⓘ" con tooltip al hover.
 * Usa createPortal + position:fixed para escapar de overflow-hidden del padre.
 */

import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Info } from 'lucide-react';

interface InfoTooltipProps {
    text: string;
    theme?: 'dark' | 'light';
    size?: 'sm' | 'md';
}

const InfoTooltip: React.FC<InfoTooltipProps> = ({
    text,
    theme = 'dark',
    size = 'sm',
}) => {
    const [visible, setVisible] = useState(false);
    const [coords, setCoords] = useState({ top: 0, left: 0 });
    const btnRef = useRef<HTMLButtonElement>(null);
    const isDark = theme === 'dark';
    const iconSize = size === 'md' ? 'w-4 h-4' : 'w-3.5 h-3.5';

    const updatePosition = () => {
        if (!btnRef.current) return;
        const rect = btnRef.current.getBoundingClientRect();
        setCoords({
            top: rect.top - 8,               // un poco arriba del ícono
            left: rect.left + rect.width / 2, // centrado horizontal
        });
    };

    const handleEnter = () => { updatePosition(); setVisible(true); };
    const handleLeave = () => setVisible(false);

    useEffect(() => {
        if (!visible) return;
        const onScroll = () => updatePosition();
        window.addEventListener('scroll', onScroll, { passive: true });
        return () => window.removeEventListener('scroll', onScroll);
    }, [visible]);

    const tooltip = visible ? (
        <div
            style={{
                position: 'fixed',
                top: coords.top,
                left: coords.left,
                transform: 'translate(-50%, -100%)',
                zIndex: 9999,
                pointerEvents: 'none',
            }}
        >
            <div
                className={`
                    w-56 px-3 py-2.5 rounded-xl text-xs font-medium leading-relaxed shadow-2xl
                    ${isDark
                        ? 'bg-slate-800 text-slate-200 border border-slate-700/80'
                        : 'bg-slate-700 text-white border border-slate-600/80'
                    }
                `}
            >
                {text}
                {/* Flecha apuntando hacia el ícono */}
                <span
                    className="absolute top-full left-1/2 -translate-x-1/2"
                    style={{
                        width: 0, height: 0,
                        borderLeft: '5px solid transparent',
                        borderRight: '5px solid transparent',
                        borderTop: `5px solid ${isDark ? '#1e293b' : '#374151'}`,
                    }}
                />
            </div>
        </div>
    ) : null;

    return (
        <span className="relative inline-flex items-center">
            <button
                ref={btnRef}
                type="button"
                className={`
                    inline-flex items-center justify-center rounded-full
                    transition-colors duration-150 cursor-default outline-none
                    ${isDark
                        ? 'text-slate-600 hover:text-slate-400'
                        : 'text-slate-400 hover:text-slate-500'
                    }
                `}
                onMouseEnter={handleEnter}
                onMouseLeave={handleLeave}
                onFocus={handleEnter}
                onBlur={handleLeave}
                aria-label="Más información"
            >
                <Info className={iconSize} />
            </button>

            {/* Portal: escapa cualquier overflow-hidden del stack de padres */}
            {typeof document !== 'undefined' && createPortal(tooltip, document.body)}
        </span>
    );
};

export default InfoTooltip;
